/**
 * Speed-dial slash commands: templated dispatch only. Selecting a command
 * prefills the composer with a natural-language prompt (no new backend
 * surface) — `<angle-bracket>` spans mark the placeholder the user overtypes
 * before sending normally.
 */
export interface SlashCommand {
  /** Typed token, e.g. "/compare". */
  command: string
  /** One-line description shown in the speed-dial popup. */
  description: string
  /** Natural-language prompt prefilled into the composer. */
  template: string
}

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    command: "/deltas",
    description: "How did my runs do this week vs last?",
    template:
      "How did my runs from this week compare to last week — any metrics or test cases that regressed?",
  },
  {
    command: "/compare",
    description: "Compare two runs case-by-case",
    template:
      "Compare run <baseline run id or name> to run <candidate run id or name> — what flipped?",
  },
  {
    command: "/diagnose",
    description: "Diagnose the worst failing case in a run",
    template:
      "Diagnose the worst failing case in run <run id or name> — walk me through what went wrong and the most likely root cause.",
  },
  {
    command: "/audit",
    description: "Audit my evaluation setup",
    template:
      "Audit my evaluation setup — agents, test sets, personas, and metrics. What's missing, redundant, or misconfigured?",
  },
  {
    command: "/coverage",
    description: "Review a test set's coverage",
    template:
      "Review the coverage of test set <test set id or name> — which scenarios and edge cases am I missing?",
  },
]

const PLACEHOLDER_PATTERN = /<[^<>\n]+>/

/** First `<placeholder>` span (selected for overtype), else caret-at-end. */
export function getTemplateSelection(template: string): {
  start: number
  end: number
} {
  const match = PLACEHOLDER_PATTERN.exec(template)
  return match
    ? { start: match.index, end: match.index + match[0].length }
    : { start: template.length, end: template.length }
}

/**
 * Commands matching the current draft. The menu only opens while the draft is
 * a bare command token ("/" as the FIRST character, no spaces yet).
 */
export function matchSlashCommands(draft: string): SlashCommand[] {
  if (!/^\/[a-z]*$/i.test(draft)) {
    return []
  }
  const query = draft.toLowerCase()
  return SLASH_COMMANDS.filter((command) => command.command.startsWith(query))
}
